import { useState } from 'react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { SecretInput } from '@reflex/ui/components/forms/SecretInput';
import { Label } from '@reflex/ui/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { cn } from '@reflex/ui/lib/utils';

/**
 * A single tier the create form can target. The picker builds this list
 * from the active owner scope (launch dialog / persona owner) and the
 * caller's permissions; the form just renders whatever it's handed.
 *
 * `id` is opaque to the form — the picker uses it to dispatch to the
 * right mutation. Pre-defined kinds:
 *
 *   - 'org'  → admin org secrets (`PUT /admin/secrets/:name`)
 *   - 'user' → caller's user-tier secrets (`POST /me/secrets`)
 *   - 'team' → team-tier secrets, `id` is `team:<teamId>`
 *             (`POST /organizations/:orgId/teams/:teamId/secrets`)
 */
export interface CreateSecretTier {
  id: string;
  kind: 'org' | 'user' | 'team';
  label: string;
  /** Team id for `kind: 'team'` rows; ignored otherwise. */
  teamId?: string;
}

interface SecretCreateFormProps {
  /** Pre-fill the name field. Useful when the form is opened in the
   *  context of a search term the user already typed. */
  initialName?: string;
  isPending?: boolean;
  onSubmit: (name: string, value: string, tier: CreateSecretTier | undefined) => void;
  onCancel: () => void;
  /** Helper text rendered under the name input. */
  nameHint?: string;
  className?: string;
  /**
   * Optional tier choices to render above the name field. When omitted
   * the form renders without a selector — preserves the existing
   * admin-secrets-page caller behavior (which only writes org-tier).
   *
   * When exactly one option is provided the selector still renders so
   * the operator can see what tier the secret will land in, but it's
   * disabled and pre-selected.
   */
  tierOptions?: CreateSecretTier[];
  /** Id of the initially-selected tier. Defaults to `tierOptions[0]`. */
  defaultTierId?: string;
}

/**
 * Form body for creating a custom secret. The value is stored encrypted
 * in the app's vault and mirrored into devboxes as an env var at launch.
 * Owns its own state, validation, and Cancel/Save footer — the wrapper
 * (dialog or popover) provides the title/description header.
 *
 * Used by:
 *   - {@link AddSecretPopover} (admin / security / team secrets pages)
 *   - the {@link SecretRefPicker} inline-create flow, which passes
 *     `tierOptions` so the operator can pick Personal / Team / Org.
 */
export function SecretCreateForm({
  initialName = '',
  isPending = false,
  onSubmit,
  onCancel,
  nameHint = 'Uppercase letters, digits, and underscores only.',
  className,
  tierOptions,
  defaultTierId,
}: SecretCreateFormProps) {
  const [name, setName] = useState(() => sanitizeName(initialName));
  const [value, setValue] = useState('');
  const [tierId, setTierId] = useState<string>(() => {
    if (!tierOptions || tierOptions.length === 0) return '';
    if (defaultTierId && tierOptions.some((o) => o.id === defaultTierId)) return defaultTierId;
    return tierOptions[0].id;
  });

  // Derive the effective selection at render time so a parent rebuilding
  // `tierOptions` (e.g. the picker's owner scope changes while the
  // popover is open) doesn't leave the form holding a stale id. Plain
  // derivation avoids the lint rule against `setState` in effects.
  const selectedTier: CreateSecretTier | undefined = (() => {
    if (!tierOptions || tierOptions.length === 0) return undefined;
    const match = tierOptions.find((o) => o.id === tierId);
    if (match) return match;
    if (defaultTierId) {
      const dft = tierOptions.find((o) => o.id === defaultTierId);
      if (dft) return dft;
    }
    return tierOptions[0];
  })();
  const effectiveTierId = selectedTier?.id ?? '';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedValue = value.trim();
    if (!trimmedName || !trimmedValue) return;
    onSubmit(trimmedName, trimmedValue, selectedTier);
  }

  const tierRequired = (tierOptions?.length ?? 0) > 0;
  const tierOk = !tierRequired || Boolean(selectedTier);
  const canSubmit = Boolean(name.trim()) && Boolean(value.trim()) && tierOk && !isPending;

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('space-y-4', className)}
      data-testid="secret-create-form"
    >
      {tierOptions && tierOptions.length > 0 ? (
        <div className="space-y-2">
          <Label htmlFor="secret-create-form-tier">Tier</Label>
          <Select
            value={effectiveTierId}
            onValueChange={setTierId}
            disabled={tierOptions.length === 1}
          >
            <SelectTrigger id="secret-create-form-tier" className="w-full" size="sm">
              <SelectValue placeholder="Select a tier" />
            </SelectTrigger>
            <SelectContent>
              {tierOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <div className="space-y-2">
        <Label htmlFor="secret-create-form-name">Name</Label>
        <Input
          id="secret-create-form-name"
          placeholder="MY_SECRET_KEY"
          value={name}
          onChange={(e) => setName(sanitizeName(e.target.value))}
          autoComplete="off"
          autoFocus={!initialName}
        />
        {nameHint ? <p className="text-xs text-muted-foreground">{nameHint}</p> : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="secret-create-form-value">Value</Label>
        <SecretInput
          id="secret-create-form-value"
          placeholder="Enter secret value…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus={Boolean(initialName)}
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function sanitizeName(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9_]/g, '');
}
